import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import { ButtonFilled } from "./UIButtons";

const SNAPSHOT_COUNT = 3;

const FaceRegistration = ({
  registeredFaces,
  setRegisteredFaces = () => {},
  onVerified = () => {},
  setOpen,
  type,
}) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [verificationResult, setVerificationResult] = useState("");
  const [capturing, setCapturing] = useState(false);

  // Dynamically get model path based on environment
  const getModelUrl = () => {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    return isLocal
      ? process.env.REACT_APP_MODEL_PATH_DEV || "/models"
      : // process.env.REACT_APP_MODEL_PATH_PROD ||
        // "https://www.dev.app.system.tymeplushr.com/models";
        "http://192.168.1.6:3000/models";
  };

  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = getModelUrl();

      console.log("Loading models from:", MODEL_URL);

      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(
          `${MODEL_URL}/tiny_face_detector`
        ),
        faceapi.nets.faceLandmark68Net.loadFromUri(
          `${MODEL_URL}/face_landmark_68`
        ),
        faceapi.nets.faceRecognitionNet.loadFromUri(
          `${MODEL_URL}/face_recognition`
        ),
      ]);

      console.log("working");
      setModelsLoaded(true);
      console.log("✅ Models loaded");
    };

    loadModels();
  }, []);

  const stopWebcam = () => {
    if (webcamRef.current && webcamRef.current.video.srcObject) {
      webcamRef.current.video.srcObject
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const drawBox = (detection) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = 640;
    canvas.height = 480;
    context.clearRect(0, 0, canvas.width, canvas.height);

    if (!detection || !detection.detection) return;

    const { x, y, width, height } = detection.detection.box;
    context.strokeStyle = "#00FF00";
    context.lineWidth = 2;
    context.strokeRect(x, y, width, height);
  };

  const getFaceDescriptor = async () => {
    if (
      webcamRef.current &&
      webcamRef.current.video.readyState === 4 &&
      modelsLoaded
    ) {
      const detection = await faceapi
        .detectSingleFace(
          webcamRef.current.video,
          new faceapi.TinyFaceDetectorOptions()
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        drawBox(detection);
        return detection.descriptor;
      }
    }
    return null;
  };

  const registerFace = async () => {
    setCapturing(true);
    const descriptors = [];

    for (let i = 0; i < SNAPSHOT_COUNT; i++) {
      const descriptor = await getFaceDescriptor();
      if (descriptor) {
        descriptors.push(Array.from(descriptor));
      } else {
        alert("❌ Failed to capture face. Try again.");
        setCapturing(false);
        return;
      }
      await new Promise((res) => setTimeout(res, 700));
    }

    setRegisteredFaces([{ descriptors }]);
    setVerificationResult("✅ Face registered.");
    setCapturing(false);
    stopWebcam();
    setOpen(false);
  };

  const verifyFace = async () => {
    const descriptor = await getFaceDescriptor();
    if (!descriptor) {
      setVerificationResult("❌ No face detected.");
      return;
    }

    if (!registeredFaces || registeredFaces.length === 0) {
      setVerificationResult("❌ No registered face to compare.");
      return;
    }

    const descriptorsArray = registeredFaces.map(
      (desc) => new Float32Array(desc)
    );

    const labeledDescriptor = new faceapi.LabeledFaceDescriptors(
      "stored",
      descriptorsArray
    );

    const faceMatcher = new faceapi.FaceMatcher([labeledDescriptor], 0.45);
    const bestMatch = faceMatcher.findBestMatch(descriptor);

    setVerificationResult(
      bestMatch.label !== "unknown"
        ? "✅ Face verified."
        : "❌ Face not recognized."
    );

    stopWebcam();
    setOpen(false);
    onVerified();
  };

  return (
    <div style={{ padding: "1rem", textAlign: "center" }}>
      {modelsLoaded ? (
        <div
          style={{
            position: "relative",
            width: 640,
            height: 480,
            margin: "0 auto",
          }}
        >
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={640}
            height={480}
            videoConstraints={{ width: 640, height: 480 }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 640,
              height: 480,
            }}
          />
        </div>
      ) : (
        <p>Loading models... Please wait.</p>
      )}

      <div style={{ marginTop: 20 }}>
        {type === "register" && (
          <ButtonFilled
            type="primary"
            onClick={registerFace}
            disabled={capturing || !modelsLoaded}
            loading={capturing}
          >
            {capturing ? "Registering..." : "Register"}
          </ButtonFilled>
        )}

        {type === "verify" && (
          <ButtonFilled
            type="primary"
            onClick={verifyFace}
            disabled={!modelsLoaded}
          >
            Verify Face
          </ButtonFilled>
        )}
      </div>

      {verificationResult && (
        <p style={{ marginTop: 20, fontWeight: "bold" }}>
          {verificationResult}
        </p>
      )}
    </div>
  );
};

export default FaceRegistration;
